import { useMemo } from 'react';
import { DetailsPanel, Info, Tooltip, Typography } from '@weareopenr/component-library';
import styles from './ProductCategoriesPanel.module.scss';
import { ICategory, IMenuItem } from 'src/types/menu.types';

export interface ProductCategoriesPanelProps {
  item: IMenuItem;
}

/**
 * Flattens a nested category structure into a breadcrumb array
 * Recursively collects parent categories from root to leaf
 */
const buildCategoryBreadcrumbs = (category: ICategory): string[] => {
  const breadcrumbs: string[] = [];
  let current: ICategory | null | undefined = category;

  while (current) {
    breadcrumbs.unshift(current.name);
    current = current.parent;
  }

  return breadcrumbs;
};

export const ProductCategoriesPanel = ({ item }: ProductCategoriesPanelProps) => {
  const categories: ICategory[] = item.categories || [];

  const categoryPaths = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        breadcrumbs: buildCategoryBreadcrumbs(category),
      })),
    [categories],
  );

  if (categoryPaths.length === 0) {
    return null;
  }

  return (
    <DetailsPanel
      title="Product Categories"
      tooltip={
        <Tooltip text="These categories are synced in through RME. In order to edit or change these, please do so in RME then re-sync into Openr">
          <Info />
        </Tooltip>
      }
    >
      <div className={styles['container']}>
        <ul className={styles['list']}>
          {categoryPaths.map((categoryPath) => (
            <li key={categoryPath.id} className={styles['category-hierarchy']}>
              {categoryPath.breadcrumbs.map((breadcrumb, index) => (
                <Typography key={`${categoryPath.id}-${index}`} className={styles['ancestor']}>
                  {breadcrumb}
                  {index < categoryPath.breadcrumbs.length - 1 && <span aria-hidden="true"> &gt; </span>}
                </Typography>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </DetailsPanel>
  );
};
